"use client";
import React, { memo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import useUserPosts from "../../../../feed/hooks/useUserPosts";
import useAuthSession from "../../../../authPatient/hooks/useAuthSession";
import { fetchUserById } from "../../../../Profile/redux/thunks/userThunks";
import "./MedicalQuestions.css";
import ErreurImage from "@/assets/icons/search.png";

// ─── fetch comment count for a single post ───────────────────────────────────
// Reuses the same endpoint as useComments — adjust the URL to match your API
async function fetchCommentCount(postId) {
  if (!postId) return 0;
  try {
    // Try the most common REST patterns — keep the one that matches your backend
    const res = await fetch(`/api/posts/${postId}/comments`);
    if (!res.ok) return 0;
    const data = await res.json();
    // data may be an array or { data: [...], total: N }
    if (Array.isArray(data)) return data.length;
    if (Array.isArray(data?.data)) return data.data.length;
    if (typeof data?.total === "number") return data.total;
    if (typeof data?.count === "number") return data.count;
    return 0;
  } catch {
    return 0;
  }
}

// ─── hook: resolves counts for a list of postIds ─────────────────────────────
function useCommentCounts(postIds = []) {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (!postIds.length) return;

    let cancelled = false;

    Promise.all(
      postIds.map(async (id) => {
        const n = await fetchCommentCount(id);
        return [id, n];
      })
    ).then((results) => {
      if (cancelled) return;
      setCounts(Object.fromEntries(results));
    });

    return () => {
      cancelled = true;
    };
  // stringify so effect only re-runs when the actual ids change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIds.join(",")]);

  return counts;
}

// ─────────────────────────────────────────────────────────────────────────────

function MedicalQuestions({ title, onSelect }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const allUsers = useSelector((state) => state.user?.users || {});

  const { effectiveUserId } = useAuthSession({ auto: true, once: false });
  const currentUserId = effectiveUserId || null;

  const { posts, loading, hasPosts, fetchPosts } = useUserPosts(currentUserId);

  useEffect(() => {
    if (!currentUserId) return;
    fetchPosts();
  }, [fetchPosts, currentUserId]);

  // Fetch user data for post owners
  useEffect(() => {
    if (!posts || posts.length === 0) return;
    const userIds = new Set();
    posts.forEach((post) => {
      const ownerId =
        post?.postOwnerId || post?.user?._id || post?.user?.id ||
        post?.userId || post?.user_id || post?.ownerId ||
        post?.authorId || post?.createdBy;
      if (ownerId && !allUsers[ownerId]) userIds.add(ownerId);
    });
    userIds.forEach((uid) => dispatch(fetchUserById(uid)));
  }, [posts, allUsers, dispatch]);

  // ✅ Collect postIds and fetch comment counts for each
  const postIds = (posts || []).map((p) => p.id_post || p.postId || p.id).filter(Boolean);
  const commentCounts = useCommentCounts(postIds);

  const handleSeeAll = () => navigate("/medical-questionspage");

  const data = Array.isArray(posts)
    ? posts.map((p) => {
        const postId = p.id_post || p.postId || p.id;

        const ownerId =
          p?.postOwnerId || p?.user?._id || p?.user?.id ||
          p?.userId || p?.user_id || p?.ownerId ||
          p?.authorId || p?.createdBy;

        const ownerData = ownerId ? allUsers[String(ownerId)] : null;

        // ✅ Use fetched count — not a missing field on the post object
        const answers = commentCounts[postId] ?? 0;

        return {
          id: postId || Math.random().toString(),
          date: new Date(p.createdAt || p.date || Date.now()).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          }),
          text: p.description || t("medicalQuestions.fallback.noContent"),
          answers,
          status: p.status || "pending",
          authorName:
            ownerData?.firstName && ownerData?.lastName
              ? `${ownerData.firstName} ${ownerData.lastName}`.trim()
              : p?.user?.firstName && p?.user?.lastName && p?.user?.firstName !== "Inconnu"
              ? `${p.user.firstName} ${p.user.lastName}`.trim()
              : t("medicalQuestions.fallback.user"),
          authorRole: ownerData?.role || p?.user?.role || p?.role,
        };
      })
    : [];

  const label = (n) =>
    t(n === 1 ? "medicalQuestions.answers.one" : "medicalQuestions.answers.other", { count: n });

  const chipClass = (n) => (n === 0 ? "mq-chip mq-chip--warn" : "mq-chip mq-chip--ok");

  return (
    <div className="mq">
      <div className="mq__head">
        <div className="mq__title">
          <span>{t("medicalQuestions.title")}</span>
          {hasPosts && (
            <span style={{ marginLeft: "8px", fontSize: "0.9em", opacity: 0.7 }}>
              ({posts.length})
            </span>
          )}
        </div>
        <button className="mq__see" onClick={handleSeeAll} type="button">
          <span>{t("medicalQuestions.seeAll")}</span>
          <RightOutlined />
        </button>
      </div>

      <div className="mq__list">
        {loading ? (
          <div className="mq__loading">{t("medicalQuestions.loading")}</div>
        ) : hasPosts && data.length > 0 ? (
          data.slice(0, 3).map((item, index) => (
            <button
              key={item.id || `post-${index}`}
              className="mq__item"
              onClick={() => onSelect?.(item)}
              title={item.text}
              type="button"
            >
              <div className="mq__date">{item.date}</div>
              <div className="mq__text">
                {item.text.length > 100 ? `${item.text.substring(0, 100)}...` : item.text}
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px", gap: "8px" }}>
                <div style={{ fontSize: "0.85em", opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t("medicalQuestions.by", { name: item.authorName })}
                </div>
                <span className={chipClass(item.answers)}>{label(item.answers)}</span>
              </div>
            </button>
          ))
        ) : (
          <div className="mq__empty">
            <img className="img-err" src={ErreurImage} alt="Empty" />
          </div>
        )}
      </div>

      {hasPosts && posts.length > 3 && (
        <div style={{ textAlign: "center", marginTop: "10px", fontSize: "0.9em", opacity: 0.7 }}>
          {t("medicalQuestions.more", { count: posts.length - 3 })}
        </div>
      )}
    </div>
  );
}

MedicalQuestions.propTypes = {
  title: PropTypes.string,
  onSelect: PropTypes.func,
};

export default memo(MedicalQuestions);
